import {
  Box,
  Card,
  Flex,
  Heading,
  Spinner,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  Bar,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { makeRequest } from "@/network/api";

export type InitialClaimsByHourlyData = {
  hour: string;
  hourly_claim_count: number;
  hourly_claim_amount: number;
};

export type BattleGroupClaimData = {
  battle_group_won_at: string;
  total_users: number;
  claimed_users: number;
  total_unlock: number;
  total_claimed: number;
};

export type AirdropData = {
  invitedUsers: {
    totalUsers: number;
    totalTokens: number;
    deferredUsers: number;
    deferredTokens: number;
    nonDeferredUsers: number;
    nonDeferredTokens: number;
    initialMainnetTokens: number;
    initialDeferredTokens: number;
    initialNonDeferredTokens: number;
  };
  totalClaims: {
    claimedUsers: number;
    claimedTokens: number;
    deferredClaimedUsers: number;
    deferredClaimedTokens: number;
    nonDeferredClaimedUsers: number;
    nonDeferredClaimedTokens: number;
  };
  initialClaims: {
    initialClaimedUsers: number;
    initialClaimedTokens: number;
    initialDeferredClaimedUsers: number;
    initialDeferredClaimedTokens: number;
    initialNonDeferredClaimedUsers: number;
    initialNonDeferredClaimedTokens: number;
  };
  mainnetClaims: {
    mainnetClaimedUsers: number;
    mainnetClaimedTokens: number;
    mainnetDeferredClaimedUsers: number;
    mainnetDeferredClaimedTokens: number;
    mainnetNonDeferredClaimedUsers: number;
    mainnetNonDeferredClaimedTokens: number;
  };
  initialClaimsByHourlyData: InitialClaimsByHourlyData[];
  battleGroupClaimData: BattleGroupClaimData[];
};

type ApiResponse = {
  stats: AirdropData;
};

export const AirdropSummary = () => {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAirdropStats = async () => {
      try {
        setIsLoading(true);
        const response = await makeRequest("/admin/airdrop/stats");
        const responseData = await response.json();
        setData(responseData.data);
      } catch (error) {
        console.error("Error fetching airdrop stats:", error);
        setError("Failed to load airdrop statistics");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAirdropStats();
  }, []);

  const formatNumber = (num: number) => num.toLocaleString();
  const formatPercentage = (numerator: number, denominator: number) =>
    denominator > 0 ? ((numerator / denominator) * 100).toFixed(1) : "0.0";

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="50vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4} maxW="1200px" mx="auto">
        <Heading mb={6}>Airdrop Summary</Heading>
        <Card.Root p={4}>
          <Card.Body>
            <Text color="red.500">{error}</Text>
          </Card.Body>
        </Card.Root>
      </Box>
    );
  }

  const stats = data?.stats;

  if (!stats) {
    return (
      <Box p={4} maxW="1200px" mx="auto">
        <Heading mb={6}>Airdrop Summary</Heading>
        <Card.Root p={4}>
          <Card.Body>
            <Text>No data available</Text>
          </Card.Body>
        </Card.Root>
      </Box>
    );
  }

  // Convert hour strings to timestamps for proper time series handling
  const chartData = stats.initialClaimsByHourlyData.map((item) => ({
    ...item,
    timestamp: new Date(item.hour).getTime(),
    hourDisplay: item.hour,
  }));

  // Calculate max values for axis domains
  const maxTokenAmount = Math.max(
    ...chartData.map((d) => d.hourly_claim_amount),
  );
  const maxClaimCount = Math.max(...chartData.map((d) => d.hourly_claim_count));

  // Add 10% padding to max values
  const tokenAmountDomain = [0, maxTokenAmount * 1.1];
  const claimCountDomain = [0, maxClaimCount * 1.1];

  // Calculate time domain with padding
  const timestamps = chartData.map((d) => d.timestamp);
  const minTimestamp = Math.min(...timestamps);
  const maxTimestamp = Math.max(...timestamps);
  const timeRange = maxTimestamp - minTimestamp;
  const timePadding = timeRange * 0.05; // 5% padding on each side
  const timeDomain = [minTimestamp - timePadding, maxTimestamp + timePadding];

  return (
    <Box p={0} maxW="1200px" mx="auto">
      <Heading mb={6}>Airdrop Summary</Heading>

      {/* Summary Statistics */}
      <Card.Root p={4} mb={6}>
        <Card.Body>
          <Stack gap={4}>
            {/* Main Initial Claims Summary */}
            <Box>
              <Text fontSize="xl" fontWeight="bold" mb={2}>
                Initial Claims:{" "}
                {formatNumber(stats.initialClaims.initialClaimedTokens)}/
                {formatNumber(stats.invitedUsers.initialMainnetTokens)} (
                {formatPercentage(
                  stats.initialClaims.initialClaimedTokens,
                  stats.invitedUsers.initialMainnetTokens,
                )}
                %) from {formatNumber(stats.initialClaims.initialClaimedUsers)}/
                {formatNumber(stats.invitedUsers.totalUsers)} (
                {formatPercentage(
                  stats.initialClaims.initialClaimedUsers,
                  stats.invitedUsers.totalUsers,
                )}
                %) users
              </Text>
            </Box>

            {/* Deferred vs Non-Deferred Breakdown */}
            <Box pl={4} borderLeft="2px solid" borderColor="gray.200">
              <Text fontSize="md" fontWeight="semibold" mb={2} color="blue.600">
                Deferred Users:{" "}
                {formatNumber(stats.initialClaims.initialDeferredClaimedTokens)}
                /{formatNumber(stats.invitedUsers.initialDeferredTokens)} (
                {formatPercentage(
                  stats.initialClaims.initialDeferredClaimedTokens,
                  stats.invitedUsers.initialDeferredTokens,
                )}
                %) from{" "}
                {formatNumber(stats.initialClaims.initialDeferredClaimedUsers)}/
                {formatNumber(stats.invitedUsers.deferredUsers)} (
                {formatPercentage(
                  stats.initialClaims.initialDeferredClaimedUsers,
                  stats.invitedUsers.deferredUsers,
                )}
                %) users
              </Text>

              <Text fontSize="md" fontWeight="semibold" color="green.600">
                Non-Deferred Users:{" "}
                {formatNumber(
                  stats.initialClaims.initialNonDeferredClaimedTokens,
                )}
                /{formatNumber(stats.invitedUsers.initialNonDeferredTokens)} (
                {formatPercentage(
                  stats.initialClaims.initialNonDeferredClaimedTokens,
                  stats.invitedUsers.initialNonDeferredTokens,
                )}
                %) from{" "}
                {formatNumber(
                  stats.initialClaims.initialNonDeferredClaimedUsers,
                )}
                /{formatNumber(stats.invitedUsers.nonDeferredUsers)} (
                {formatPercentage(
                  stats.initialClaims.initialNonDeferredClaimedUsers,
                  stats.invitedUsers.nonDeferredUsers,
                )}
                %) users
              </Text>
            </Box>
          </Stack>
        </Card.Body>
      </Card.Root>

      {/* Initial Claims by Hour Chart */}
      <Card.Root p={4} mb={6}>
        <Card.Header>
          <Heading size="md">Initial Claims by Hour</Heading>
        </Card.Header>
        <Card.Body>
          <Box height="400px">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  scale="time"
                  domain={timeDomain}
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tickFormatter={(timestamp) => {
                    const date = new Date(timestamp);
                    return date.toISOString().slice(0, 13) + ":00";
                  }}
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  domain={tokenAmountDomain}
                  tick={{ fontSize: 12 }}
                  label={{
                    value: "Token Amount",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={claimCountDomain}
                  tick={{ fontSize: 12 }}
                  label={{
                    value: "Claim Count",
                    angle: 90,
                    position: "insideRight",
                  }}
                />
                <Tooltip
                  labelFormatter={(timestamp) =>
                    `Hour: ${new Date(timestamp).toISOString().slice(0, 13)}:00`
                  }
                  formatter={(value, name) => [
                    (value as number).toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    }),
                    name === "hourly_claim_count" ? "Claims" : "Token Amount",
                  ]}
                />
                <Bar
                  yAxisId="right"
                  dataKey="hourly_claim_count"
                  fill="#3182ce"
                  fillOpacity={0.6}
                  name="hourly_claim_count"
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="hourly_claim_amount"
                  stroke="#38a169"
                  strokeWidth={3}
                  name="hourly_claim_amount"
                  connectNulls={false}
                  dot={{ fill: "#38a169", strokeWidth: 2, r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </Card.Body>
      </Card.Root>

      {/* Battle Group Claim Data Table */}
      <Card.Root p={4}>
        <Card.Header>
          <Heading size="md">Battle Group Claim Stats</Heading>
        </Card.Header>
        <Card.Body>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Battle Group Won At</Table.ColumnHeader>
                <Table.ColumnHeader>Total Users</Table.ColumnHeader>
                <Table.ColumnHeader>Claimed Users</Table.ColumnHeader>
                <Table.ColumnHeader>Total Unlock</Table.ColumnHeader>
                <Table.ColumnHeader>Total Claimed</Table.ColumnHeader>
                <Table.ColumnHeader>Claim Rate</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {stats.battleGroupClaimData.map((battleGroup, index) => (
                <Table.Row key={index}>
                  <Table.Cell>{battleGroup.battle_group_won_at}</Table.Cell>
                  <Table.Cell>
                    {battleGroup.total_users.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell>
                    {battleGroup.claimed_users.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell>
                    {battleGroup.total_unlock.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </Table.Cell>
                  <Table.Cell>
                    {battleGroup.total_claimed.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </Table.Cell>
                  <Table.Cell>
                    {battleGroup.total_users > 0
                      ? `${((battleGroup.claimed_users / battleGroup.total_users) * 100).toFixed(1)}%`
                      : "0%"}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Card.Body>
      </Card.Root>
    </Box>
  );
};
